/**
 * Contexte et hook d'authentification
 * Partagé entre BackOffice et FrontOffice
 */

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import type { User, PortalType, LoginRequest } from '../types/auth';
import { authApi } from '../services/auth.api';

interface LoginResult {
  success: boolean;
  error?: string;
  mustChangePassword?: boolean;
  emailSent?: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  currentPortal: PortalType | null;
  login: (data: LoginRequest) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  defaultPortal?: PortalType;
}

export function AuthProvider({ children, defaultPortal }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPortal, setCurrentPortal] = useState<PortalType | null>(defaultPortal || null);

  const isAuthenticated = !!user;

  // Charger l'utilisateur au démarrage
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const portal = localStorage.getItem('currentPortal') as PortalType | null;

      if (accessToken) {
        // Utiliser le portail stocké ou le défaut
        const activePortal = portal || defaultPortal || null;
        setCurrentPortal(activePortal);

        const response = await authApi.getCurrentUser();
        if (response.data?.user) {
          setUser(response.data.user);
        } else {
          // Token invalide, nettoyer
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('currentPortal');
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, [defaultPortal]);

  const login = useCallback(async (data: LoginRequest): Promise<LoginResult> => {
    setIsLoading(true);

    const response = await authApi.login(data);

    if (response.error) {
      setIsLoading(false);
      return {
        success: false,
        error: response.error,
        mustChangePassword: response.mustChangePassword,
        emailSent: response.emailSent,
      };
    }

    if (response.data) {
      localStorage.setItem('accessToken', response.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
      localStorage.setItem('currentPortal', data.portal);
      setUser(response.data.user);
      setCurrentPortal(data.portal);
    }

    setIsLoading(false);
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      await authApi.logout(refreshToken);
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentPortal');
    setUser(null);
    setCurrentPortal(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const response = await authApi.getCurrentUser();
    if (response.data?.user) {
      setUser(response.data.user);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        currentPortal,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
